/**
 * Deciding whether a recipe's word for a food and the catalogue's word for it
 * mean the same thing.
 *
 * Lithuanian nouns decline, so the two almost never share a spelling: a page
 * asks for "300 g aguonų" and the catalogue holds "Aguonos". Trigram similarity
 * scores that pair 0.5 — the same score it gives "margarinas" and "Marinara
 * padažas", which is how the recipe importer came to offer a pasta sauce for a
 * block of margarine. No threshold separates those two cases, because the
 * measure itself is answering the wrong question.
 *
 * The right question is about heads. Two food names name the same food when
 * their head nouns agree, and Lithuanian puts the head last: "Saulėgrąžų
 * aliejus" is an oil and "Obuolių sultys" is a juice, not an apple. Everything
 * before the head narrows it, so a catalogue row carrying modifiers the cook
 * never mentioned is a narrower product than the one asked for — worth
 * offering, not worth choosing on their behalf. And a name that mentions the
 * ingredient only after a preposition — "varškės sūrelis su aguonomis" — is a
 * different food that happens to contain it.
 *
 * The stemmer is deliberately shallow. It strips case endings and stops; it
 * does not attempt derivation, so "aguona" and "aguonos" collapse together
 * while "aguonos" and "aguoninis" do not. That is the right depth for a
 * catalogue, where over-stemming silently merges two foods that differ.
 */

/** Folds diacritics and case, so "Aguonų" and "aguonu" are one word. */
export function foldLt(value: string): string {
  return value.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
}

/**
 * The noun form a Lithuanian numeral demands: "1 diena", "3 dienos", "11
 * dienų".
 *
 * Three forms, not two, and the rule is about the last digit rather than the
 * size of the number — 21 takes the singular, 11 does not, and every teen goes
 * to the third form. Written out because interpolating a count into a sentence
 * is something the voice catalogue does constantly, and getting it wrong is
 * the exact kind of clumsiness that makes copy read as machine-written.
 */
export function pluralLt(count: number, one: string, few: string, many: string): string {
  const n = Math.abs(Math.trunc(count))
  const lastTwo = n % 100
  if (lastTwo >= 11 && lastTwo <= 19) return many
  const last = n % 10
  if (last === 1) return one
  if (last >= 2 && last <= 9) return few
  return many
}

/** "3 dienos" / "11 dienų" — the count and its noun, agreed. Nominative. */
export function daysLt(count: number): string {
  return `${count} ${pluralLt(count, 'diena', 'dienos', 'dienų')}`
}

/**
 * The genitive of the same phrase: "po 3 dienų", "9 dienų tylos".
 *
 * A count governs the noun's number, but the sentence around it governs the
 * case, and Lithuanian will not let one stand in for the other. "Po 9 dienos"
 * is the mistake a translation engine makes; a person says "po 9 dienų". The
 * genitive plural is the same word for every count above one, which is why
 * this is a two-branch function and `daysLt` is a three-branch one.
 */
export function daysLtGen(count: number): string {
  return `${count} ${pluralLt(count, 'dienos', 'dienų', 'dienų')}`
}

/** The accusative: "9 dienas tylėjai", "visas 3 dienas". */
export function daysLtAcc(count: number): string {
  return `${count} ${pluralLt(count, 'dieną', 'dienas', 'dienų')}`
}

/**
 * Nominative → vocative, so the app can say someone's name to them.
 *
 * Lithuanian addresses people in a case of its own: "Labas, Saulius" is the
 * mistake a foreigner makes, and hearing it from your own phone every morning
 * is worse than not being greeted at all. Only the four endings that cover
 * ordinary given names are handled, longest ending first; anything else — a
 * foreign name, an initial, a nickname already in the vocative — is left
 * exactly as it was typed, which is the one safe answer.
 */
export function vocativeLt(name: string): string {
  const trimmed = name.trim()
  if (trimmed.length < 3) return trimmed
  // Only the first word: "Saulius Muzikevičius" is greeted as "Sauliau".
  const first = trimmed.split(/\s+/)[0] ?? trimmed
  const lower = first.toLocaleLowerCase('lt')

  // -ius is listed before -us because "Saulius" ends in both and only the
  // longer one gives "Sauliau" rather than "Sauliuau".
  for (const [ending, vocative] of VOCATIVE_ENDINGS) {
    if (lower.endsWith(ending) && first.length - ending.length >= 2) {
      return first.slice(0, -ending.length) + vocative
    }
  }
  return first
}

const VOCATIVE_ENDINGS: ReadonlyArray<readonly [string, string]> = [
  ['ius', 'iau'], // Saulius  → Sauliau
  ['as', 'ai'], // Eimantas → Eimantai
  ['is', 'i'], // Kęstutis → Kęstuti
  ['us', 'au'], // Vytautus → Vytautau
  ['ė', 'e'], // Gintarė  → Gintare
  ['a', 'a'], // Rūta stays Rūta
]

/**
 * Case endings, longest first, in their folded spelling (ų → u, ė → e).
 *
 * Sorted at load rather than by hand: the list grows by appending, and an
 * ending that is a suffix of a longer one has to be tried second or "aguonomis"
 * loses only its "s".
 */
const ENDINGS: readonly string[] = [
  'iausiuose',
  'iausiomis',
  'iausiems',
  'iausiais',
  'iausias',
  'iausio',
  'iausia',
  'iuose',
  'iomis',
  'ioms',
  'uose',
  'iais',
  'iams',
  'omis',
  'oms',
  'emis',
  'imis',
  'umis',
  'ieji',
  'iaus',
  'iose',
  'ose',
  'yse',
  'ese',
  'uje',
  'yje',
  'ame',
  'uju',
  'aus',
  'ius',
  'ias',
  'ies',
  'ims',
  'ems',
  'ams',
  'ums',
  'iai',
  'iam',
  'iem',
  'ai',
  'ei',
  'ui',
  'io',
  'ia',
  'iu',
  'ie',
  'us',
  'is',
  'ys',
  'as',
  'es',
  'os',
  'a',
  'e',
  'i',
  'o',
  'u',
  'y',
].toSorted((a, b) => b.length - a.length)

/**
 * Shortest stem an ending may leave behind.
 *
 * Three, because "medus" has to reach "med" to meet "medaus" and "sūris" has to
 * reach "sur" to meet "sūrio". Below that the stems stop identifying anything:
 * two letters would put "sultys" and "sūris" in the same bucket.
 */
const MIN_STEM = 3

export function stemLt(word: string): string {
  for (const ending of ENDINGS) {
    if (word.length - ending.length >= MIN_STEM && word.endsWith(ending)) {
      return word.slice(0, -ending.length)
    }
  }
  return word
}

/**
 * Words that carry no food in them. `is` is "iš" folded, not the English verb —
 * an English catalogue name never reaches this list on its own.
 */
const STOP = new Set([
  'su',
  'be',
  'is',
  'ir',
  'i',
  'ant',
  'po',
  'prie',
  'arba',
  'bei',
  'iki',
  'nuo',
])

/**
 * Words after which the name stops describing the head and starts listing what
 * was added to it. "Sūrelis su aguonomis" is a curd bar; the poppy seeds are a
 * flavouring and must not be allowed to become the head noun.
 */
const PHRASE_BREAK = new Set(['su', 'be', 'is', 'ir', 'arba', 'bei'])

function wordsOf(name: string): string[] {
  return foldLt(name)
    .split(/[^a-z]+/)
    .filter((word) => word.length >= 2)
}

interface Parsed {
  /** The last word before any preposition — what the name is a kind of. */
  head: string | null
  /** What narrows it: "saulėgrąžų" in "saulėgrąžų aliejus". */
  modifiers: string[]
  /** Every content word, including those after a preposition. */
  all: string[]
}

export function parseFoodName(name: string): Parsed {
  const raw = wordsOf(name)
  const cut = raw.findIndex((word) => PHRASE_BREAK.has(word))
  const main = (cut === -1 ? raw : raw.slice(0, cut)).filter((word) => !STOP.has(word)).map(stemLt)
  const all = raw.filter((word) => !STOP.has(word)).map(stemLt)
  return { head: main.at(-1) ?? null, modifiers: main.slice(0, -1), all }
}

/**
 * How confidently a catalogue name answers an ingredient line, 0 to 1.
 *
 * 1.0   the same food, no unasked-for narrowing — "aguonų" / "Aguonos"
 * 0.6+  the right kind, narrower than asked — "aliejaus" / "Saulėgrąžų aliejus"
 * 0.3   contains it, is not it — "aguonų" / "Varškės sūrelis su aguonomis"
 * 0     unrelated — "margarinas" / "Marinara padažas"
 */
export function nameMatchStrength(query: string, name: string): number {
  const q = parseFoodName(query)
  const n = parseFoodName(name)
  if (q.head == null || n.head == null) return 0

  if (q.head === n.head) {
    if (n.modifiers.length === 0) return 1
    // Modifiers the cook did name are agreement; ones they did not are a
    // narrower product than the line asked for.
    const asked = new Set([q.head, ...q.modifiers])
    const agreed = n.modifiers.filter((word) => asked.has(word)).length
    // Silence and disagreement are not the same thing. "Aliejaus" names no oil
    // in particular, so sunflower answers it; "kepimo milteliai" names one
    // powder in particular, and cocoa powder is a different tin. A line that
    // brought its own modifier and met none of the row's has been contradicted,
    // not merely narrowed, so it drops below the fill-it-in threshold.
    if (agreed === 0 && q.modifiers.length > 0) return 0.45
    return 0.6 + 0.4 * (agreed / n.modifiers.length)
  }

  return n.all.includes(q.head) ? 0.3 : 0
}

/**
 * A catalogue row carries up to two names; either may be the one that matches,
 * and the better of the two is the row's answer.
 */
export function foodMatchStrength(
  query: string,
  food: { nameLt?: string | null; nameEn?: string | null },
): number {
  return Math.max(
    food.nameLt ? nameMatchStrength(query, food.nameLt) : 0,
    food.nameEn ? nameMatchStrength(query, food.nameEn) : 0,
  )
}

/**
 * Enough agreement to fill the line in without being asked.
 *
 * Set just under the "right kind, narrower than asked" band, because a generic
 * oil for "2 šaukštai aliejaus" is the answer the cook meant even though the
 * catalogue calls it sunflower. Below this a candidate is still shown — it is
 * simply not chosen for them.
 */
export const AUTO_MATCH_FLOOR = 0.55

/** Below this a row shares no word with the line and is not worth showing. */
export const SHOW_MATCH_FLOOR = 0.3
